import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import Tabs from '@atlaskit/tabs';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { QueryIntent } from '../../constants';

import { messages } from './messages';
import { DebuggerTabList } from './tab-list';
import { LoopPanel } from './tab-panels/loop-panel';
import { OrchestrationOverviewPanel } from './tab-panels/orchestration-overview-panel';
import { PluginInvocationsPanel } from './tab-panels/plugin-invocations-panel';
import { RankedSearchResultsPanel } from './tab-panels/ranked-search-results-panel';
import { RawJsonPanel } from './tab-panels/raw-json-panel';
import { RawSearchResultsPanel } from './tab-panels/raw-search-results-panel';
import { RequestOverviewPanel } from './tab-panels/request-overview-panel';
import { normalizeDebuggerData } from './utils';

export type OnlineDebuggerModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function OnlineDebuggerModal({
	isOpen,
	onClose,
}: OnlineDebuggerModalProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { commonAttributes, smartAnswersRoute } = useAIAnswerContext();
	const isNaturalLanguageQuery = smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY;

	const { extraAttributes, sources } = commonAttributes;
	const debuggerData = useMemo(
		() =>
			normalizeDebuggerData(
				extraAttributes,
				fg('kd_consolidate_sources_analytics') ? sources : undefined,
			),
		[extraAttributes, sources],
	);

	const showPluginsPanels =
		isNaturalLanguageQuery &&
		!debuggerData.minions.hasMinionOutputs &&
		debuggerData.pluginInvocations.hasPluginInvocations;

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="x-large" testId="sain-online-debugger-modal">
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Tabs id="sain-online-debugger-tabs">
							<DebuggerTabList
								isNaturalLanguageQuery={isNaturalLanguageQuery}
								hasMinionOutputs={debuggerData.minions.hasMinionOutputs}
								hasPluginInvocations={debuggerData.pluginInvocations.hasPluginInvocations}
								roundCount={debuggerData.minions.roundCount}
							/>
							<RequestOverviewPanel
								{...debuggerData.requestOverview}
								smartAnswersRoute={smartAnswersRoute}
								isNaturalLanguageQuery={isNaturalLanguageQuery}
							/>
							{showPluginsPanels && (
								<RawSearchResultsPanel searchResults={debuggerData.rawSearch.searchResults} />
							)}
							{showPluginsPanels && (
								<RankedSearchResultsPanel
									rankedDocumentsWithChunks={debuggerData.rankedSearch.rankedDocumentsWithChunks}
								/>
							)}
							{showPluginsPanels && (
								<PluginInvocationsPanel
									nonSearchPluginInvocations={
										debuggerData.pluginInvocations.nonSearchPluginInvocations
									}
								/>
							)}
							{isNaturalLanguageQuery && (
								<OrchestrationOverviewPanel
									orchestrationRounds={debuggerData.minions.orchestrationRounds}
								/>
							)}
							{isNaturalLanguageQuery &&
								debuggerData.minions.hasMinionOutputs &&
								debuggerData.minions.orchestrationRounds.map((round, index) => (
									<LoopPanel key={`loop-panel-${index}`} round={round} roundIndex={index} />
								))}
							{isNaturalLanguageQuery && <RawJsonPanel rawJson={debuggerData.rawJson.rawJson} />}
						</Tabs>
					</ModalBody>
					<ModalFooter />
				</Modal>
			)}
		</ModalTransition>
	);
}
